import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Link2, Plus, Trash2, Play, Search, Music, ChevronRight, Star, Loader2, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = API_URL;

const MixChainsManager = ({ 
  isOpen, 
  onClose, 
  token, 
  playlistId,  // Para aplicar cadenas a un evento
  currentPlaylistSongs = [],  // Canciones actuales de la lista para seleccionar posición
  onChainApplied,  // Callback cuando se aplica una cadena
  selectedSongs = [],  // Canciones seleccionadas para crear cadena
  mode = 'browse'  // 'browse' | 'create'
}) => {
  const [chains, setChains] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEventType, setFilterEventType] = useState('');
  const [selectedChain, setSelectedChain] = useState(null);
  const [isCreating, setIsCreating] = useState(mode === 'create');
  const [isApplying, setIsApplying] = useState(false);
  const [insertAfterSong, setInsertAfterSong] = useState(null);  // Canción después de la cual insertar
  
  // Form para crear nueva cadena
  const [newChainForm, setNewChainForm] = useState({
    name: '',
    description: '',
    event_types: '',
    tags: ''
  });

  // Cargar cadenas al abrir
  useEffect(() => {
    if (isOpen && mode === 'browse') {
      fetchChains();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: fetchChains
  }, [isOpen, mode]);

  const fetchChains = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterEventType) params.append('event_type', filterEventType);
      
      const response = await axios.get(
        `${API}/api/dj-assistant/mix-chains?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setChains(response.data.chains || []);
    } catch (error) {
      console.error('Error fetching mix chains:', error);
      toast.error('Error al cargar cadenas de mezcla');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    fetchChains();
  };

  const handleSelectChain = async (chain) => {
    try {
      const response = await axios.get(
        `${API}/api/dj-assistant/mix-chains/${chain.id}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSelectedChain(response.data);
    } catch (error) {
      console.error('Error loading chain details:', error);
      toast.error('Error al cargar detalles de la cadena');
    }
  };

  const handleApplyChain = async (chainId, position = 'end') => {
    if (!playlistId) {
      toast.error('No hay evento seleccionado');
      return;
    }
    
    setIsApplying(true);
    try {
      // Si hay una canción seleccionada para insertar después, usar su índice
      let finalPosition = position;
      if (insertAfterSong && currentPlaylistSongs.length > 0) {
        const afterIndex = currentPlaylistSongs.findIndex(s => s.id === insertAfterSong);
        if (afterIndex !== -1) {
          finalPosition = String(afterIndex + 1);  // Insertar después de esa posición
        }
      }
      
      const response = await axios.post(
        `${API}/api/dj-assistant/mix-chains/${chainId}/apply/${playlistId}?position=${finalPosition}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        if (response.data.songs_not_found?.length > 0) {
          toast.warning(
            `${response.data.songs_not_found.length} canciones no están en tu biblioteca`,
            { duration: 5000 }
          );
        }
        
        if (onChainApplied) {
          onChainApplied(response.data);
        }
        
        setInsertAfterSong(null);
        onClose();
      }
    } catch (error) {
      console.error('Error applying chain:', error);
      toast.error(error.response?.data?.detail || 'Error al aplicar cadena');
    } finally {
      setIsApplying(false);
    }
  };

  const handleDeleteChain = async (chainId) => {
    if (!confirm('¿Eliminar esta cadena de mezcla?')) return;
    
    try {
      await axios.delete(
        `${API}/api/dj-assistant/mix-chains/${chainId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success('Cadena eliminada');
      setSelectedChain(null);
      fetchChains();
    } catch (error) {
      console.error('Error deleting chain:', error);
      toast.error('Error al eliminar cadena');
    }
  };

  const handleCreateChain = async () => {
    if (!newChainForm.name.trim()) {
      toast.error('Introduce un nombre para la cadena');
      return;
    }
    
    if (selectedSongs.length < 2) {
      toast.error('Selecciona al menos 2 canciones');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/dj-assistant/mix-chains/from-selection`,
        {
          name: newChainForm.name,
          description: newChainForm.description,
          event_types: newChainForm.event_types.split(',').map(s => s.trim()).filter(Boolean),
          tags: newChainForm.tags.split(',').map(s => s.trim()).filter(Boolean),
          song_ids: selectedSongs.map(s => s.id)
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setNewChainForm({ name: '', description: '', event_types: '', tags: '' });
        setIsCreating(false);
        onClose();
      }
    } catch (error) {
      console.error('Error creating chain:', error);
      toast.error(error.response?.data?.detail || 'Error al crear cadena');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-base">
            <Link2 className="w-5 h-5 text-cyan-400" />
            {isCreating ? 'Guardar Cadena' : 'Cadenas de Mezcla'}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            {isCreating 
              ? 'Guarda esta secuencia para reutilizarla'
              : 'Secuencias que funcionan bien juntas'}
          </DialogDescription>
        </DialogHeader>

        {/* Modo Crear */}
        {isCreating && (
          <div className="space-y-4">
            {/* Preview de canciones seleccionadas */}
            <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700">
              <p className="text-sm text-slate-400 mb-2">
                Canciones en la cadena ({selectedSongs.length}):
              </p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {selectedSongs.map((song, index) => (
                  <div key={song.id} className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500 w-5">{index + 1}.</span>
                    <span className="text-white">{song.title || song.titulo}</span>
                    <span className="text-slate-500">-</span>
                    <span className="text-slate-400">{song.artist || song.artista}</span>
                    {index < selectedSongs.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-cyan-500 ml-auto" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Formulario */}
            <div className="space-y-3">
              <div>
                <Label htmlFor="chain-name" className="text-white">Nombre *</Label>
                <Input
                  id="chain-name"
                  placeholder="Ej: Apertura Boda Joven, Hora Loca Latino..."
                  value={newChainForm.name}
                  onChange={(e) => setNewChainForm({...newChainForm, name: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="chain-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="chain-desc" className="text-white">Descripcion</Label>
                <Textarea
                  id="chain-desc"
                  placeholder="Describe cuando usar esta cadena..."
                  value={newChainForm.description}
                  onChange={(e) => setNewChainForm({...newChainForm, description: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="chain-events" className="text-white">Tipos de evento</Label>
                  <Input
                    id="chain-events"
                    placeholder="Boda, Cumpleaños..."
                    value={newChainForm.event_types}
                    onChange={(e) => setNewChainForm({...newChainForm, event_types: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">Separados por coma</p>
                </div>
                <div>
                  <Label htmlFor="chain-tags" className="text-white">Tags</Label>
                  <Input
                    id="chain-tags"
                    placeholder="reggaeton, subir-energia..."
                    value={newChainForm.tags}
                    onChange={(e) => setNewChainForm({...newChainForm, tags: e.target.value})}
                    className="bg-slate-800 border-slate-700 text-white text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">Separados por coma</p>
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => {
                  setIsCreating(false);
                  if (mode === 'create') onClose();
                }}
                variant="outline"
                className="flex-1 border-slate-600 text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateChain}
                disabled={isLoading || !newChainForm.name.trim() || selectedSongs.length < 2}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="save-chain-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Cadena
              </Button>
            </div>
          </div>
        )}

        {/* Modo Navegar */}
        {!isCreating && (
          <div className="space-y-4">
            {/* Barra de búsqueda */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-slate-800 border-slate-700 text-white flex-1"
              />
              <div className="flex gap-2">
                <select
                  value={filterEventType}
                  onChange={(e) => setFilterEventType(e.target.value)}
                  className="px-2 py-2 bg-slate-800 border border-slate-700 text-white text-xs sm:text-sm rounded flex-1 sm:flex-none"
                >
                  <option value="">Todos</option>
                  <option value="Boda">Boda</option>
                  <option value="Cumpleaños">Cumple</option>
                  <option value="Corporativo">Corp</option>
                  <option value="Comunión">Comunión</option>
                </select>
                <Button onClick={handleSearch} className="bg-slate-700 hover:bg-slate-600" size="sm">
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Lista de cadenas o detalle */}
            {selectedChain ? (
              // Vista detallada de una cadena
              <div className="space-y-4">
                <Button 
                  onClick={() => setSelectedChain(null)}
                  variant="ghost"
                  className="text-slate-400 hover:text-white"
                >
                  ← Volver a la lista
                </Button>
                
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{selectedChain.name}</h3>
                      {selectedChain.description && (
                        <p className="text-sm text-slate-400 mt-1">{selectedChain.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleDeleteChain(selectedChain.id)}
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-600/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mb-4">
                    {selectedChain.event_types?.map(et => (
                      <span key={et} className="px-2 py-1 bg-cyan-600/30 text-cyan-300 rounded text-xs">
                        {et}
                      </span>
                    ))}
                    {selectedChain.tags?.map(tag => (
                      <span key={tag} className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Canciones de la cadena */}
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400">
                      {selectedChain.songs?.length} canciones:
                    </p>
                    {selectedChain.songs?.map((song, index) => (
                      <div 
                        key={index} 
                        className="flex items-center gap-3 p-2 bg-slate-900/50 rounded"
                      >
                        <span className="text-slate-500 w-6 text-center">{index + 1}</span>
                        <Music className="w-4 h-4 text-cyan-500" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{song.title}</p>
                          <p className="text-slate-400 text-xs truncate">{song.artist}</p>
                        </div>
                        {song.bpm && (
                          <span className="text-xs text-slate-500">{song.bpm} BPM</span>
                        )}
                        {index < selectedChain.songs.length - 1 && (
                          <ChevronRight className="w-4 h-4 text-cyan-500" />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Botón aplicar */}
                  {playlistId && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <p className="text-sm text-slate-400 mb-2">Aplicar a la lista:</p>
                      
                      {/* Selector de posición */}
                      {currentPlaylistSongs.length > 0 && (
                        <div className="mb-3">
                          <label className="text-xs text-slate-500 block mb-1">Insertar después de:</label>
                          <select
                            value={insertAfterSong || ''}
                            onChange={(e) => setInsertAfterSong(e.target.value || null)}
                            className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 text-white text-sm rounded"
                          >
                            <option value="">-- Al final de la lista --</option>
                            {currentPlaylistSongs.map((song, idx) => (
                              <option key={song.id} value={song.id}>
                                {idx + 1}. {song.title || song.titulo} - {song.artist || song.artista}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApplyChain(selectedChain.id, 'start')}
                          disabled={isApplying}
                          size="sm"
                          variant="outline"
                          className="border-slate-600 text-white hover:bg-slate-700"
                        >
                          Al inicio
                        </Button>
                        <Button
                          onClick={() => handleApplyChain(selectedChain.id, insertAfterSong ? 'after' : 'end')}
                          disabled={isApplying}
                          className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                          data-testid="apply-chain-btn"
                        >
                          {isApplying ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 mr-2" />
                          )}
                          {insertAfterSong ? 'Insertar aquí' : 'Al final'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Lista de cadenas
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {isLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mx-auto" />
                    <p className="text-slate-400 mt-2">Cargando cadenas...</p>
                  </div>
                ) : chains.length === 0 ? (
                  <div className="text-center py-8">
                    <Link2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No hay cadenas guardadas</p>
                    <p className="text-slate-500 text-sm mt-1">
                      Selecciona canciones en tu lista y guárdalas como cadena
                    </p>
                  </div>
                ) : (
                  chains.map((chain) => (
                    <div
                      key={chain.id}
                      onClick={() => handleSelectChain(chain)}
                      className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-cyan-500/50 cursor-pointer transition-colors"
                      data-testid={`chain-item-${chain.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium">{chain.name}</h4>
                          <p className="text-xs text-slate-500 mt-1">
                            {chain.songs_preview?.join(' → ')}
                            {chain.num_songs > 3 && ` ... (+${chain.num_songs - 3} más)`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          {chain.times_used > 0 && (
                            <span className="flex items-center gap-1 text-xs text-amber-400">
                              <Star className="w-3 h-3 fill-amber-400" />
                              {chain.times_used}
                            </span>
                          )}
                          <span className="text-xs text-slate-500">
                            {chain.num_songs} canciones
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1 mt-2">
                        {chain.event_types?.slice(0, 2).map(et => (
                          <span key={et} className="px-1.5 py-0.5 bg-cyan-600/20 text-cyan-400 rounded text-[10px]">
                            {et}
                          </span>
                        ))}
                        {chain.tags?.slice(0, 2).map(tag => (
                          <span key={tag} className="px-1.5 py-0.5 bg-slate-700/50 text-slate-400 rounded text-[10px]">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Botón crear nueva */}
            {!selectedChain && (
              <Button
                onClick={() => setIsCreating(true)}
                variant="outline"
                className="w-full border-dashed border-slate-600 text-slate-400 hover:text-white hover:border-cyan-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear nueva cadena
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MixChainsManager;
